// React
import React, { useContext, useRef } from "react";

import { MoreVert } from "@mui/icons-material";
// MUI
import { IconButton, Tooltip } from "@mui/material";

// Third party imports
import { useTranslation } from "react-i18next";

// Styles
import { colors } from "styles/tokens";

// Relative imports
import { ContextMenuStateContext } from "./context";
import useContextMenu from "./useContextMenu";

const stop = (event) => event.stopPropagation();

/**
 * The ⋮ button that opens the same menu as the right-click, under the button. It is the way in
 * for touch and keyboard users, so keep it on every element that has a context menu.
 * Clicks, pointer and key events do not reach the parent (card onClick, dnd-kit, grid cell
 * selection).
 *
 * @param {object} props
 * @param {Array|Function} props.items menu items, or a function returning them (called on open)
 * @param {"small"|"medium"} [props.size]
 * @param {string} [props.ariaLabel] defaults to "Altre azioni"
 * @param {string} [props.className] e.g. "hover-action" to show it on hover/focus only
 * @param {object} [props.menuOptions] options of openFrom ({ data, align, onClose })
 * @param {React.ElementType} [props.icon] defaults to MoreVert
 * @param {boolean} [props.tooltip] show the label as tooltip (default true)
 * @param {boolean} [props.disabled]
 * @param {object} [props.sx]
 */
export default function ActionMenuButton({
  items,
  size = "small",
  ariaLabel,
  className,
  menuOptions,
  icon: Icon = MoreVert,
  tooltip = true,
  disabled = false,
  sx,
}) {
  const { t } = useTranslation("ui");
  const { openFrom } = useContextMenu();
  const { open, anchorEl } = useContext(ContextMenuStateContext);
  const buttonRef = useRef(null);
  const expanded = open && !!anchorEl && anchorEl === buttonRef.current;
  const label = ariaLabel || t("contextMenu.moreActions");

  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    openFrom(event.currentTarget, items, menuOptions);
  };

  // Enter/Space would also reach a clickable parent (role="button" cards)
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") event.stopPropagation();
  };

  const button = (
    <IconButton
      ref={buttonRef}
      size={size}
      aria-label={label}
      aria-haspopup="menu"
      aria-expanded={expanded}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onPointerDown={stop}
      onMouseDown={stop}
      onTouchStart={stop}
      onDoubleClick={stop}
      className={[className, expanded ? "is-active" : ""].filter(Boolean).join(" ") || undefined}
      sx={{
        color: colors.gray500,
        "&:hover, &.is-active": { color: colors.gray700, backgroundColor: colors.gray100 },
        ...sx,
      }}
    >
      <Icon fontSize="small" />
    </IconButton>
  );

  if (!tooltip || disabled) return button;
  return (
    <Tooltip title={label} disableInteractive>
      {button}
    </Tooltip>
  );
}
